import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type GenerateInvoiceRequest,
  type Invoice,
  type InvoiceStatusValue,
  type InvoiceSummary,
  type OrderForInvoicing,
  type CancelInvoiceRequest,
  type Order,
  type OrderChargeCost,
  type RaiseCreditNoteRequest,
  type UpdateDraftInvoiceRequest,
} from '../client';
import { ordersForApprovalKeys } from './useOrdersForApproval';

export const invoiceKeys = {
  all: ['tms', 'invoice'] as const,
  list: (organisationId: string) => ['tms', 'invoice', 'org', organisationId] as const,
  byId: (id: string) => ['tms', 'invoice', 'id', id] as const,
};

export const ordersForInvoicingKeys = {
  all: ['tms', 'ordersForInvoicing'] as const,
  byOrg: (organisationId: string) => ['tms', 'ordersForInvoicing', 'org', organisationId] as const,
};

/** Approved orders with un-invoiced charges — the Create-invoices queue. */
export function useOrdersForInvoicing(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<OrderForInvoicing[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderForInvoicing[], Error>({
    queryKey: organisationId ? ordersForInvoicingKeys.byOrg(organisationId) : ordersForInvoicingKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getOrdersForInvoicing(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** Generated invoices for an org (summary rows). */
export function useInvoices(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<InvoiceSummary[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<InvoiceSummary[], Error>({
    queryKey: organisationId ? invoiceKeys.list(organisationId) : invoiceKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getInvoices(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** A single invoice with its lines. */
export function useInvoice(
  id: string | null,
  options?: Omit<UseQueryOptions<Invoice, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Invoice, Error>({
    queryKey: id ? invoiceKeys.byId(id) : invoiceKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No invoice id');
      return tmsApi.getInvoice(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Generate an invoice. Invalidates the org's Create-invoices queue (the billed
 * orders drop out / shrink) and the invoice list (the new invoice appears).
 */
export function useGenerateInvoice() {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, GenerateInvoiceRequest>({
    mutationKey: [...invoiceKeys.all, 'generate'],
    mutationFn: (body) => tmsApi.generateInvoice(body),
    onSuccess: (_invoice, { organisationId }) => {
      qc.invalidateQueries({ queryKey: ordersForInvoicingKeys.byOrg(organisationId) });
      qc.invalidateQueries({ queryKey: invoiceKeys.list(organisationId) });
    },
  });
}

/** Advance an invoice's status (issue / send / mark paid). */
export function useSetInvoiceStatus(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, { id: string; status: InvoiceStatusValue }>({
    mutationKey: [...invoiceKeys.all, 'set-status'],
    mutationFn: ({ id, status }) => tmsApi.setInvoiceStatus(id, status),
    onSuccess: (invoice) => {
      if (organisationId) qc.invalidateQueries({ queryKey: invoiceKeys.list(organisationId) });
      qc.invalidateQueries({ queryKey: invoiceKeys.byId(invoice.id) });
    },
  });
}

/**
 * Email an invoice to its customer's billing contacts (Send and Resend). The
 * list refreshes because a first send moves the row from Issued to Sent.
 */
export function useSendInvoiceEmail(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, { id: string }>({
    mutationKey: [...invoiceKeys.all, 'send-email'],
    mutationFn: ({ id }) => tmsApi.sendInvoiceEmail(id),
    onSuccess: (invoice) => {
      if (organisationId) qc.invalidateQueries({ queryKey: invoiceKeys.list(organisationId) });
      qc.invalidateQueries({ queryKey: invoiceKeys.byId(invoice.id) });
    },
  });
}

/**
 * Void an invoice. Invalidates the ORDER queues as well as the invoice list:
 * cancelling releases the charges it consumed and drops the orders it billed
 * back to Approved, so they reappear in Create — a stale queue would show work
 * as still invoiced that is now waiting to be billed again.
 */
export function useCancelInvoice(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, { id: string } & CancelInvoiceRequest>({
    mutationKey: [...invoiceKeys.all, 'cancel'],
    mutationFn: ({ id, ...body }) => tmsApi.cancelInvoice(id, body),
    onSuccess: (invoice) => {
      if (organisationId) {
        qc.invalidateQueries({ queryKey: invoiceKeys.list(organisationId) });
        qc.invalidateQueries({ queryKey: ordersForInvoicingKeys.byOrg(organisationId) });
        qc.invalidateQueries({ queryKey: ordersForApprovalKeys.byOrg(organisationId) });
      }
      qc.invalidateQueries({ queryKey: invoiceKeys.byId(invoice.id) });
    },
  });
}

/**
 * Bill one charge to someone else. The Create queue regroups by payer, so it
 * refreshes; the order's charge list is only marked stale — refetching it now
 * would rehydrate the order screen's panel over any unsaved edits.
 */
export function useSetChargeBillTo(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<OrderChargeCost, Error, { chargeCostId: string; partyId: string | null }>({
    mutationKey: [...ordersForInvoicingKeys.all, 'bill-to'],
    mutationFn: ({ chargeCostId, partyId }) => tmsApi.setChargeBillTo(chargeCostId, partyId),
    onSuccess: (charge) => {
      if (organisationId) qc.invalidateQueries({ queryKey: ordersForInvoicingKeys.byOrg(organisationId) });
      qc.invalidateQueries({ queryKey: ['tms', 'orderChargeCost', 'order', charge.orderId], refetchType: 'none' });
    },
  });
}

/** Invoice the whole order to someone else (or back to its customer). */
export function useSetOrderInvoiceTo(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<Order, Error, { orderId: string; partyId: string | null }>({
    mutationKey: [...ordersForInvoicingKeys.all, 'invoice-to'],
    mutationFn: ({ orderId, partyId }) => tmsApi.setOrderInvoiceTo(orderId, partyId),
    onSuccess: (order) => {
      if (organisationId) qc.invalidateQueries({ queryKey: ordersForInvoicingKeys.byOrg(organisationId) });
      qc.setQueryData(['tms', 'order', 'id', order.id], order);
    },
  });
}

/** Mark exported / not exported. Only the invoice itself changes. */
export function useSetInvoiceExport(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, { id: string; exported: boolean; reference?: string | null }>({
    mutationKey: [...invoiceKeys.all, 'export'],
    mutationFn: ({ id, ...body }) => tmsApi.setInvoiceExport(id, body),
    onSuccess: (invoice) => {
      if (organisationId) qc.invalidateQueries({ queryKey: invoiceKeys.list(organisationId) });
      qc.setQueryData(invoiceKeys.byId(invoice.id), invoice);
    },
  });
}

/**
 * Save an edited draft. Removed lines hand their charges back and added ones
 * take charges, so both order queues refresh with the invoice.
 */
export function useUpdateDraftInvoice(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, { invoiceId: string } & UpdateDraftInvoiceRequest>({
    mutationKey: [...invoiceKeys.all, 'update-draft'],
    mutationFn: ({ invoiceId, ...body }) => tmsApi.updateDraftInvoice(invoiceId, body),
    onSuccess: (invoice) => {
      if (organisationId) {
        qc.invalidateQueries({ queryKey: invoiceKeys.list(organisationId) });
        qc.invalidateQueries({ queryKey: ordersForInvoicingKeys.byOrg(organisationId) });
        qc.invalidateQueries({ queryKey: ordersForApprovalKeys.byOrg(organisationId) });
      }
      qc.setQueryData(invoiceKeys.byId(invoice.id), invoice);
    },
  });
}

/**
 * Raise a credit note. Refreshes the invoice list, the credited invoice, and —
 * since released charges reappear in Create — both order queues.
 */
export function useRaiseCreditNote(organisationId: string | null) {
  const qc = useQueryClient();
  return useMutation<Invoice, Error, { invoiceId: string } & RaiseCreditNoteRequest>({
    mutationKey: [...invoiceKeys.all, 'credit-note'],
    mutationFn: ({ invoiceId, ...body }) => tmsApi.raiseCreditNote(invoiceId, body),
    onSuccess: (_note, { invoiceId }) => {
      if (organisationId) {
        qc.invalidateQueries({ queryKey: invoiceKeys.list(organisationId) });
        qc.invalidateQueries({ queryKey: ordersForInvoicingKeys.byOrg(organisationId) });
        qc.invalidateQueries({ queryKey: ordersForApprovalKeys.byOrg(organisationId) });
      }
      qc.invalidateQueries({ queryKey: invoiceKeys.byId(invoiceId) });
    },
  });
}

/** Send an approved order back to For-approval — refreshes both queues. */
export function useUnapproveOrderForInvoicing() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; orderId: string }>({
    mutationKey: [...ordersForInvoicingKeys.all, 'unapprove'],
    mutationFn: (body) => tmsApi.unapproveOrderForInvoicing(body),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: ordersForInvoicingKeys.byOrg(organisationId) });
      qc.invalidateQueries({ queryKey: ordersForApprovalKeys.byOrg(organisationId) });
    },
  });
}
